import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, FolderOpen, Sparkles, Loader2, RefreshCw, ImageIcon, Check, Maximize2, Plus, Minus } from 'lucide-react';
import { webhookService, projectService } from '../../services/api';

export interface InsertableImage {
  src: string;
  alt: string;
  title: string;
  origin: 'wp-media' | 'base64';
  wpId?: number;
}

interface ImageSourceSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (image: InsertableImage) => void;
  projectId?: string;
  sessionId?: string; // Session ID for saving images
  articleTitle?: string;
  brief?: any;
  isFeaturedImage?: boolean; // Nueva prop para imagen destacada
}

type TabType = 'wp' | 'bank' | 'ai';

const normalizeBase64 = (img: any): string => {
  // Si no es string, intentar extraer URL del objeto
  if (!img) return '';
  if (typeof img !== 'string') {
    // Si es un objeto, intentar extraer la URL
    if (typeof img === 'object') {
      const extracted = img.url || img.src || img.output || img.image || img.data || '';
      if (typeof extracted === 'string') return normalizeBase64(extracted);
      return '';
    }
    return '';
  }
  if (img.startsWith('http')) return img;
  if (img.startsWith('data:image')) return img;
  if (img.startsWith('/9j/')) return `data:image/jpeg;base64,${img}`;
  if (img.startsWith('R0lGOD')) return `data:image/gif;base64,${img}`;
  return `data:image/png;base64,${img}`;
};

export const ImageSourceSelector: React.FC<ImageSourceSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  projectId,
  sessionId,
  articleTitle,
  brief,
  isFeaturedImage = false
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('wp');
  const [searchTerm, setSearchTerm] = useState('');

  // WordPress Media State
  const [wpImages, setWpImages] = useState<any[]>([]);
  const [wpLoading, setWpLoading] = useState(false);
  const [wpLoadingMore, setWpLoadingMore] = useState(false);
  const [wpError, setWpError] = useState('');
  const [wpPage, setWpPage] = useState(1);
  const [wpHasMore, setWpHasMore] = useState(true);
  const wpScrollRef = useRef<HTMLDivElement>(null);

  // External Bank State
  const [bankImages, setBankImages] = useState<string[]>([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState('');
  const [bankImageCount, setBankImageCount] = useState(1); // Cantidad de imágenes a buscar (por defecto 1)
  const [bankPage, setBankPage] = useState(1); // Página actual para paginación
  const [lastBankSearchTerm, setLastBankSearchTerm] = useState(''); // Último término buscado

  // AI Generation State
  const [aiImages, setAiImages] = useState<string[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiIncludeInfographics, setAiIncludeInfographics] = useState(false);
  const [aiImageCount, setAiImageCount] = useState(3); // Cantidad de imágenes a generar
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');

  // Preview Modal State
  const [previewImage, setPreviewImage] = useState<{
    src: string;
    type: 'wp' | 'bank' | 'ai';
    data?: any; // For WP images, stores full image object
  } | null>(null);

  // Block body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (previewImage) {
          setPreviewImage(null);
        } else {
          onClose();
        }
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, previewImage]);

  // Load WordPress images with pagination
  const loadWpImages = useCallback(async (page = 1, search = '', append = false) => {
    if (!projectId) {
      setWpError('No hay proyecto seleccionado');
      return;
    }

    if (append) {
      setWpLoadingMore(true);
    } else {
      setWpLoading(true);
      setWpImages([]);
    }
    setWpError('');

    try {
      const media = await projectService.getWordPressMedia(projectId, page, search);
      const newImages = media || [];

      if (append) {
        setWpImages(prev => [...prev, ...newImages]);
      } else {
        setWpImages(newImages);
      }

      setWpPage(page);
      // Si recibimos menos de 100 imágenes, no hay más
      setWpHasMore(newImages.length >= 100);
    } catch (err: any) {
      // Check if it's a 404 (no credentials configured)
      if (err?.response?.status === 404) {
        setWpError('WordPress no está configurado para este proyecto. Usa las pestañas "Banco" o "IA" para buscar imágenes.');
      } else {
        setWpError('Error al cargar la biblioteca de WordPress');
      }
    } finally {
      setWpLoading(false);
      setWpLoadingMore(false);
    }
  }, [projectId]);

  // Infinite scroll handler
  const handleWpScroll = useCallback(() => {
    if (!wpScrollRef.current || wpLoading || wpLoadingMore || !wpHasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = wpScrollRef.current;
    // Cargar más cuando estamos a 200px del final
    if (scrollHeight - scrollTop - clientHeight < 200) {
      const nextPage = wpPage + 1;
      loadWpImages(nextPage, searchTerm, true);
    }
  }, [wpLoading, wpLoadingMore, wpHasMore, wpPage, searchTerm, loadWpImages]);

  // Load external bank images con paginación optimizada
  // page: página de resultados (para evitar descargar imágenes repetidas)
  // append: si true, añade a las existentes; si false, reemplaza
  const loadBankImages = useCallback(async (search: string, limit: number = 1, page: number = 1, append: boolean = false) => {
    if (!search.trim()) return;

    setBankLoading(true);
    setBankError('');

    try {
      const response = await webhookService.fetchExternalImages(search, limit, undefined, page);

      let extractedImages: string[] = [];

      if (response.urls && Array.isArray(response.urls)) {
        extractedImages = response.urls;
      } else if (response.images && Array.isArray(response.images)) {
        extractedImages = response.images;
      } else if (response.data) {
        if (Array.isArray(response.data)) {
          extractedImages = response.data;
        } else if (typeof response.data === 'string') {
          extractedImages = [response.data];
        }
      } else if (Array.isArray(response)) {
        extractedImages = response.map((item: any) => {
          if (typeof item === 'string') return item;
          return item.url || item.output || item.image || item.src || item.data || null;
        }).filter((url: string | null) => url !== null) as string[];
      }

      const normalizedImages = extractedImages.map(normalizeBase64);

      // Si append=true, añadir a las existentes; si no, reemplazar
      if (append) {
        setBankImages(prev => [...prev, ...normalizedImages]);
      } else {
        setBankImages(normalizedImages);
      }

      // Guardar imágenes externas en la sesión para recuperarlas después
      if (projectId && sessionId && normalizedImages.length > 0) {
        try {
          await projectService.saveSessionImages(projectId, sessionId, normalizedImages.map(img => ({
            image_data: img,
            image_source: 'external' as const,
            alt_text: search || articleTitle || '',
            title: search || articleTitle || ''
          })));
        } catch (saveError) {
          // No bloquear el flujo si falla el guardado
        }
      }
    } catch (err) {
      setBankError('Error al buscar en el banco de imágenes');
    } finally {
      setBankLoading(false);
    }
  }, [projectId, sessionId, articleTitle]);

  // Generate AI images
  const generateAiImages = useCallback(async (prompt: string, includeInfographics: boolean, count: number = 3) => {
    if (!prompt.trim()) return;

    setAiLoading(true);
    setAiError('');

    try {
      // Determinar qué tipo de imágenes generar
      const imageType = includeInfographics ? 'all' : 'images';

      const response = await webhookService.generateImages(prompt, undefined, undefined, undefined, imageType, undefined, undefined, count);

      const allImages = [
        ...(response.images || []),
        ...(includeInfographics ? (response.infographics || []) : [])
      ].map(normalizeBase64);

      setAiImages(prev => [...prev, ...allImages]);

      // Guardar imágenes generadas en la sesión para recuperarlas después
      if (projectId && sessionId && allImages.length > 0) {
        try {
          await projectService.saveSessionImages(projectId, sessionId, allImages.map(img => ({
            image_data: img,
            image_source: 'generated' as const,
            alt_text: prompt || articleTitle || '',
            title: prompt || articleTitle || ''
          })));
        } catch (saveError) {
          // No bloquear el flujo si falla el guardado
        }
      }
    } catch (err) {
      setAiError('Error al generar imágenes con IA');
    } finally {
      setAiLoading(false);
    }
  }, [projectId, sessionId, articleTitle]);

  // Load WP images on tab change
  useEffect(() => {
    if (isOpen && activeTab === 'wp' && wpImages.length === 0) {
      setWpPage(1);
      setWpHasMore(true);
      loadWpImages(1, '');
    }
  }, [isOpen, activeTab, wpImages.length, loadWpImages]);

  // Reset pagination on modal close
  useEffect(() => {
    if (!isOpen) {
      setWpImages([]);
      setWpPage(1);
      setWpHasMore(true);
      setSearchTerm('');
    }
  }, [isOpen]);

  // Reset AI prompt when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAiPrompt('');
      setAiImages([]);
    }
  }, [isOpen]);

  const handleWpSearch = () => {
    setWpPage(1);
    setWpHasMore(true);
    loadWpImages(1, searchTerm, false);
  };

  const handleBankSearch = () => {
    const currentSearchTerm = searchTerm || articleTitle || brief?.mainKeyword || '';

    // Si el término de búsqueda cambió, resetear y buscar desde cero
    if (currentSearchTerm !== lastBankSearchTerm) {
      setLastBankSearchTerm(currentSearchTerm);
      setBankPage(1);
      setBankImages([]); // Limpiar imágenes anteriores
      loadBankImages(currentSearchTerm, bankImageCount, 1, false);
      // Actualizar página para siguiente búsqueda
      setBankPage(Math.ceil(bankImageCount / 1) + 1); // Siguiente página
      return;
    }

    // Mismo término: calcular cuántas imágenes faltan
    const currentCount = bankImages.length;
    const needed = bankImageCount - currentCount;

    if (needed <= 0) {
      // Ya tenemos suficientes o más, no hacer nada (mostrar las que hay)
      return;
    }

    // Pedir solo las que faltan desde la página actual
    loadBankImages(currentSearchTerm, needed, bankPage, true);
    // Actualizar página para siguiente búsqueda
    setBankPage(prev => prev + 1);
  };

  const handleAiGenerate = () => {
    // Si hay prompt personalizado, usarlo. Si no, usar contexto del artículo
    const contextPrompt = aiPrompt.trim() ||
      `${articleTitle || ''} ${brief?.mainKeyword || ''} ${brief?.targetKeyword || ''}`.trim();

    if (!contextPrompt) {
      setAiError('Escribe una descripción o asegúrate de tener un artículo seleccionado');
      return;
    }

    // Guardar el prompt generado para mostrar en preview
    setGeneratedPrompt(contextPrompt);
    setShowPromptPreview(true);
  };

  const handleConfirmGenerate = () => {
    setShowPromptPreview(false);
    generateAiImages(generatedPrompt, aiIncludeInfographics, aiImageCount);
  };

  const handleSelectWpImage = (image: any) => {
    const wpUrl = image.source_url || image.url || '';
    const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(wpUrl)}&id=${image.id}`;

    onSelect({
      src: proxyUrl,
      alt: image.alt_text || image.title?.rendered || '',
      title: image.title?.rendered || image.alt_text || '',
      origin: 'wp-media',
      wpId: image.id
    });
    onClose();
  };

  const handleSelectBankImage = (imageSrc: string) => {
    onSelect({
      src: imageSrc,
      alt: articleTitle || brief?.mainKeyword || 'Imagen del banco de imágenes',
      title: articleTitle || brief?.mainKeyword || 'imagen-banco',
      origin: 'base64'
    });
    onClose();
  };

  const handleSelectAiImage = (imageSrc: string) => {
    onSelect({
      src: imageSrc,
      alt: aiPrompt || articleTitle || brief?.mainKeyword || 'Imagen generada con IA',
      title: aiPrompt || articleTitle || brief?.mainKeyword || 'imagen-ia',
      origin: 'base64'
    });
    onClose();
  };

  // Preview handlers
  const handlePreviewWp = (image: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewImage({
      src: `/api/proxy-image?url=${encodeURIComponent(image.source_url || image.url)}`,
      type: 'wp',
      data: image
    });
  };

  const handlePreviewBank = (imageSrc: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewImage({
      src: imageSrc,
      type: 'bank'
    });
  };

  const handlePreviewAi = (imageSrc: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewImage({
      src: imageSrc,
      type: 'ai'
    });
  };

  const handleInsertFromPreview = () => {
    if (!previewImage) return;

    if (previewImage.type === 'wp' && previewImage.data) {
      handleSelectWpImage(previewImage.data);
    } else if (previewImage.type === 'bank') {
      handleSelectBankImage(previewImage.src);
    } else if (previewImage.type === 'ai') {
      handleSelectAiImage(previewImage.src);
    }
    setPreviewImage(null);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-600" />
            {isFeaturedImage ? 'Insertar imagen destacada' : 'Seleccionar Imagen'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('wp')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'wp'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FolderOpen className="w-4 h-4" />
            Biblioteca WordPress
          </button>
          <button
            onClick={() => setActiveTab('bank')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'bank'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Search className="w-4 h-4" />
            Banco de Imágenes
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'ai'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Generar con IA
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 p-4 flex flex-col">
          {/* WordPress Tab */}
          {activeTab === 'wp' && (
            <div className="flex-1 flex flex-col gap-4 min-h-0 overflow-hidden">
              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleWpSearch()}
                    placeholder="Buscar en biblioteca..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <button
                  onClick={handleWpSearch}
                  disabled={wpLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {wpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Buscar
                </button>
              </div>

              {/* Badge info */}
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                <Check className="w-4 h-4" />
                Ya en WordPress — no se optimiza al publicar
              </div>

              {/* Error */}
              {wpError && (
                <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm">
                  {wpError}
                </div>
              )}

              {/* Loading */}
              {wpLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              )}

              {/* Scrollable Images Grid Container */}
              <div
                ref={wpScrollRef}
                onScroll={handleWpScroll}
                className="flex-1 min-h-0 overflow-y-scroll"
                style={{ maxHeight: 'calc(90vh - 280px)' }}
              >
                {/* Images Grid */}
                {!wpLoading && wpImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {wpImages.map((image) => (
                      <div
                        key={image.id}
                        className="group relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all"
                      >
                        <img
                          src={`/api/proxy-image?url=${encodeURIComponent(image.source_url || image.url)}`}
                          alt={image.alt_text || ''}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => handleSelectWpImage(image)}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none" />

                        {/* Action buttons */}
                        <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => handlePreviewWp(image, e)}
                            className="bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                            title="Ampliar imagen"
                          >
                            <Maximize2 className="w-5 h-5 text-gray-700" />
                          </button>
                          <button
                            onClick={() => handleSelectWpImage(image)}
                            className="bg-white rounded-full p-2 shadow-lg hover:bg-green-50 transition-colors"
                            title="Insertar imagen"
                          >
                            <Check className="w-5 h-5 text-green-600" />
                          </button>
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 pointer-events-none">
                          <p className="text-white text-xs truncate">
                            {image.title?.rendered || image.alt_text || 'Sin título'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Loading more indicator */}
                {wpLoadingMore && (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-500">Cargando más imágenes...</span>
                  </div>
                )}

                {/* End of results indicator */}
                {!wpLoading && !wpLoadingMore && wpImages.length > 0 && !wpHasMore && (
                  <div className="text-center py-4 text-sm text-gray-400">
                    Has llegado al final de la biblioteca ({wpImages.length} imágenes)
                  </div>
                )}

                {/* Empty state */}
                {!wpLoading && wpImages.length === 0 && !wpError && (
                  <div className="text-center py-12 text-gray-500">
                    No se encontraron imágenes en la biblioteca
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bank Tab */}
          {activeTab === 'bank' && (
            <div className="h-full flex flex-col space-y-4 overflow-y-auto">
              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleBankSearch()}
                    placeholder="Buscar imágenes..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <button
                  onClick={handleBankSearch}
                  disabled={bankLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {bankLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Buscar
                </button>
              </div>

              {/* Cantidad de imágenes */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">Cantidad de imágenes</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setBankImageCount(Math.max(1, bankImageCount - 1))}
                      disabled={bankImageCount <= 1}
                      className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      title="Disminuir"
                    >
                      <Minus className="w-4 h-4 text-gray-600" />
                    </button>
                    <span className="text-lg font-bold text-blue-600 min-w-[2rem] text-center">{bankImageCount}</span>
                    <button
                      onClick={() => setBankImageCount(Math.min(10, bankImageCount + 1))}
                      disabled={bankImageCount >= 10}
                      className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      title="Aumentar"
                    >
                      <Plus className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Badge info */}
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                <Sparkles className="w-4 h-4" />
                Se subirá a WordPress al publicar
              </div>

              {/* Error */}
              {bankError && (
                <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm">
                  {bankError}
                </div>
              )}

              {/* Loading */}
              {bankLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              )}

              {/* Images Grid */}
              {!bankLoading && bankImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {bankImages.map((imageSrc, index) => (
                    <div
                      key={index}
                      className="group relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all"
                    >
                      <img
                        src={imageSrc}
                        alt={`Imagen ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => handleSelectBankImage(imageSrc)}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none" />

                      {/* Action buttons */}
                      <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => handlePreviewBank(imageSrc, e)}
                          className="bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                          title="Ampliar imagen"
                        >
                          <Maximize2 className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                          onClick={() => handleSelectBankImage(imageSrc)}
                          className="bg-white rounded-full p-2 shadow-lg hover:bg-green-50 transition-colors"
                          title="Insertar imagen"
                        >
                          <Check className="w-5 h-5 text-green-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!bankLoading && bankImages.length === 0 && !bankError && (
                <div className="text-center py-12 text-gray-500">
                  Busca imágenes usando el campo de arriba
                </div>
              )}
            </div>
          )}

          {/* AI Tab */}
          {activeTab === 'ai' && (
            <div className="h-full flex flex-col space-y-4 overflow-y-auto">
              {/* Prompt Preview Modal */}
              {showPromptPreview && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowPromptPreview(false)}>
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      Configurar generación
                    </h3>

                    {/* Prompt editable */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prompt (puedes editarlo)</label>
                      <textarea
                        value={generatedPrompt}
                        onChange={(e) => setGeneratedPrompt(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        rows={3}
                      />
                    </div>

                    {/* Opciones */}
                    <div className="space-y-3 mb-5">
                      {/* Cantidad de imágenes */}
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-800">Cantidad de imágenes</span>
                          <span className="text-sm font-bold text-purple-600">{aiImageCount}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="6"
                          value={aiImageCount}
                          onChange={(e) => setAiImageCount(parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                        />
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>1</span>
                          <span>3</span>
                          <span>6</span>
                        </div>
                      </div>

                      {/* Infografías */}
                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={aiIncludeInfographics}
                          onChange={(e) => setAiIncludeInfographics(e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-800">Incluir infografía</span>
                          <p className="text-xs text-gray-500">Genera también una infografía vertical además de las imágenes</p>
                        </div>
                      </label>
                    </div>

                    {/* Botones */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowPromptPreview(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleConfirmGenerate}
                        disabled={!generatedPrompt.trim()}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        Generar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Prompt input */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAiGenerate()}
                    placeholder="Describe la imagen... (o deja vacío para usar el contexto del artículo)"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <button
                  onClick={handleAiGenerate}
                  disabled={aiLoading || (!aiPrompt.trim() && !articleTitle && !brief?.mainKeyword)}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center gap-2"
                  title={!aiPrompt.trim() ? `Generará imagen basada en: ${articleTitle || brief?.mainKeyword || 'el artículo'}` : ''}
                >
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Configurar
                </button>
              </div>

              {/* Info contextual */}
              {!aiPrompt.trim() && (articleTitle || brief?.mainKeyword) && (
                <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg">
                  <ImageIcon className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Las imágenes se generarán basadas en el <strong>resumen del artículo</strong>
                  </span>
                </div>
              )}

              {/* Badge info subida */}
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                <Sparkles className="w-4 h-4" />
                Se subirá a WordPress al publicar
              </div>

              {/* Error */}
              {aiError && (
                <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm">
                  {aiError}
                </div>
              )}

              {/* Loading */}
              {aiLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  <p className="text-sm text-gray-500">Generando imágenes con IA...</p>
                </div>
              )}

              {/* Images Grid */}
              {aiImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {aiImages.map((imageSrc, index) => (
                    <div
                      key={index}
                      className="group relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-all"
                    >
                      <img
                        src={imageSrc}
                        alt={`Imagen IA ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => handleSelectAiImage(imageSrc)}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none" />

                      {/* Action buttons */}
                      <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => handlePreviewAi(imageSrc, e)}
                          className="bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                          title="Ampliar imagen"
                        >
                          <Maximize2 className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                          onClick={() => handleSelectAiImage(imageSrc)}
                          className="bg-white rounded-full p-2 shadow-lg hover:bg-green-50 transition-colors"
                          title="Insertar imagen"
                        >
                          <Check className="w-5 h-5 text-green-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!aiLoading && aiImages.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  Escribe un prompt y genera imágenes con IA
                </div>
              )}

              {/* Regenerate button */}
              {aiImages.length > 0 && !aiLoading && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={handleAiGenerate}
                    className="px-4 py-2 text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Generar más
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-8"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-4">
            {/* Close button */}
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>

            {/* Enlarged Image */}
            <img
              src={previewImage.src}
              alt="Vista previa"
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Insert button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleInsertFromPreview();
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-lg flex items-center gap-2"
            >
              <Check className="w-5 h-5" />
              Insertar esta imagen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
